/**
 * UI-3: Room Investigation View
 *
 * Operational investigation — NOT a data browser.
 * 4 zones: Situation Summary, What Led Here, Impact Signals, Recommended Actions.
 *
 * Data: existing activity ledger + replay for current state only.
 */

import { useParams, useNavigate, Link } from 'react-router-dom';
import { useInvestigation } from '../hooks/useInvestigation';
import { useActionDispatch } from '../hooks/useActionDispatch';
import { EntityViewSkeleton } from '../components/entityViews/EntityViewSkeleton';
import { SituationBanner } from '../components/entityViews/SituationBanner';
import { RecentActivity } from '../components/entityViews/RecentActivity';
import { ImpactSignals } from '../components/entityViews/ImpactSignals';
import { RecommendedActions } from '../components/entityViews/RecommendedActions';
import { ActionToast } from '../components/entityViews/ActionToast';

export function RoomViewPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();

  const { investigation, loading, error, refresh } = useInvestigation({
    entityType: 'room',
    entityId: roomId ?? '',
  });

  const { dispatch, feedback, dismissFeedback, executing } = useActionDispatch({
    entityType: 'room',
    entityId: roomId ?? '',
    onRefresh: refresh,
  });

  if (!roomId) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500">
        No room ID specified
      </div>
    );
  }

  if (loading) return <EntityViewSkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-rose-400 text-sm">{error}</div>
        <button type="button" onClick={refresh} className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  if (!investigation) return null;

  return (
    <div className="h-full flex flex-col bg-slate-900">
      {/* ── NAV BAR ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 bg-slate-800/95 backdrop-blur-sm border-b border-slate-700 px-6 py-3">
        <div className="flex items-center gap-3">
          <Link to="/rooms" className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors text-slate-400 hover:text-white" title="Back to Rooms" aria-label="Back to Rooms">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <span className="text-sm font-medium text-white">Room {roomId}</span>
          <span className="text-xs text-slate-500">Investigation</span>
          <button type="button" onClick={refresh} className="ml-auto p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors" title="Refresh">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </header>

      {/* ── 4 INVESTIGATION ZONES ──────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 max-w-3xl mx-auto w-full">
        {/* Zone 1: Situation */}
        <SituationBanner situation={investigation.situation} />

        {/* Zone 2: What Led Here */}
        <RecentActivity events={investigation.recentEvents} anomalies={investigation.anomalies} />

        {/* Zone 3: Impact */}
        <ImpactSignals
          signals={investigation.impactSignals}
          onNavigate={(entityType, entityId) => {
            if (entityType === 'room') navigate(`/rooms/${entityId}`);
            else if (entityType === 'guest') navigate(`/guests/${entityId}`);
          }}
        />

        {/* Zone 4: Actions */}
        <RecommendedActions
          actions={investigation.actions}
          disabled={executing}
          onAction={(actionType) => {
            if (actionType === 'review-requests') { navigate('/requests'); return; }
            dispatch(actionType);
          }}
        />
      </div>

      <ActionToast feedback={feedback} onDismiss={dismissFeedback} />
    </div>
  );
}
